/**
 * Keybook React hooks — server-side encryption model.
 *
 * No browser crypto. Hooks just wrap the API client; encryption happens
 * server-side via the per-tenant master key.
 */
import { useRef, useCallback, useEffect, useState } from 'react'
import { useRealtime } from '@papan/realtime/client'
import { keybookApi } from './api.js'
import type { CredentialType, EntryMetadata, KeybookEntryData } from '../core/types.js'

// ─── useKeybook: list + create + read + update + delete ─────────────

export interface UseKeybookResult {
  entries: EntryMetadata[]
  isLoading: boolean
  /** The list currently held belongs to THIS view (tenant, identity,
   *  role) — false from a switch until that view's latest request lands
   *  (success or failure), so a caller never shows or acts on another
   *  view's keys. */
  hasLoaded: boolean
  /** This view's first load failed (the list is empty because it couldn't be
   *  read, not because there are no keys). */
  loadFailed: boolean
  /** Whether the caller manages the keybook root (the server's answer). */
  managesRoot: boolean
  /** The caller's manage_keybook_permission (the server's answer, resolved once). */
  accessCapability: 'none' | 'read' | 'write'
  refreshEntries: () => Promise<void>
  /** Create an entry. By default the entry is scoped to the caller's
   *  active org email (`ownerType='email'`); pass `ownerTeamId` to
   *  create a team-owned entry instead. `fileId` (singular) is the
   *  legacy shape — `fileIds` lets callers bind several files at
   *  create time. */
  createEntry: (
    label: string,
    data: KeybookEntryData,
    options?: {
      fileId?: string
      fileIds?: string[]
      usernameHint?: string
      ownerTeamId?: string
      provider?: string
    },
  ) => Promise<string>
  decryptEntry: (entryId: string) => Promise<KeybookEntryData>
  /** The value with the version it was read at (an editor saves against it). */
  readEntryForEdit: (entryId: string) => Promise<{ data: KeybookEntryData; version: number }>
  updateEntry: (
    entryId: string,
    data: KeybookEntryData,
    /** `noteOnly`: only the note changed — saved through the note route (not a new secret). */
    options?: { label?: string; usernameHint?: string; expectedVersion?: number; noteOnly?: boolean },
  ) => Promise<void>
  deleteEntry: (entryId: string) => Promise<void>
  /** Bind an existing file to a credential. Idempotent. */
  bindFile: (entryId: string, fileId: string) => Promise<void>
  unbindFile: (entryId: string, fileId: string) => Promise<void>
}

export function useKeybook(
  tenantId: string | undefined,
  /** The org-email identity the caller is currently acting as. */
  userEmail: string | undefined,
  /** Anything else the server's answer depends on (the caller's role) — a
   *  change refetches. */
  role?: string,
): UseKeybookResult {
  const [entries, setEntries] = useState<EntryMetadata[]>([])
  const [managesRoot, setManagesRoot] = useState(false)
  const [accessCapability, setAccessCapability] = useState<'none' | 'read' | 'write'>('none')
  const [isLoading, setIsLoading] = useState(false)
  const viewKey = `${tenantId ?? ''}|${userEmail ?? ''}|${role ?? ''}`
  const [entriesFor, setEntriesFor] = useState<string | null>(null)
  const [failedFor, setFailedFor] = useState<string | null>(null)
  const entriesForRef = useRef<string | null>(null)
  /** The view shown NOW — a refresh captured by an earlier view (a mutation
   *  that finished after a switch) must not take the current view's load. */
  const viewKeyRef = useRef(viewKey)
  viewKeyRef.current = viewKey

  // Only the LATEST request may land: a tenant / identity switch, or a realtime
  // refresh racing a create's, must not let an older response arrive last (the
  // previous org's list shown under the new one).
  const requestGen = useRef(0)
  const refreshEntries = useCallback(async () => {
    const key = viewKey
    if (key !== viewKeyRef.current) return // a stale view's refresh: nothing to do
    const gen = ++requestGen.current
    const settle = (list: EntryMetadata[] | null, failed = false) => {
      setFailedFor(failed ? key : null)
      if (list) setEntries(list)
      entriesForRef.current = key
      setEntriesFor(key)
    }
    if (!tenantId) { settle([]); setIsLoading(false); return }
    setIsLoading(true)
    try {
      let res: Awaited<ReturnType<typeof keybookApi.listEntries>>
      try {
        res = await keybookApi.listEntries(tenantId)
      } catch (err) {
        // A failed load for a NEW view must not leave the previous view's
        // keys on screen under it; a failed refresh of the same view keeps them.
        if (gen === requestGen.current) {
          // A failed refresh keeps the list AND its failed flag (a failed
          // Retry after a failed first load still says it couldn't load);
          // only a successful load clears the flag.
          if (entriesForRef.current !== key) settle([], true)
        }
        throw err
      }
      if (gen !== requestGen.current) return
      const mapped: EntryMetadata[] = res.entries.map(r => ({
        id: r.id,
        tenantId: r.tenantId,
        ownerType: r.ownerType,
        ownerId: r.ownerId,
        label: r.customLabel ?? r.sourceLabel,
        sourceLabel: r.sourceLabel,
        credentialType: r.credentialType as CredentialType,
        fileIds: r.fileIds ?? [],
        files: r.files ?? [],
        customLabel: r.customLabel ?? null,
        provider: r.provider,
        // visibility: 'shared' if owned by a team, else 'private'.
        visibility: r.ownerType === 'team' ? 'shared' : 'private',
        ownerActive: r.ownerActive,
        collectionId: r.collectionId ?? null,
        permission: r.permission,
        ...(r.hiddenWithoutAdminView ? { hiddenWithoutAdminView: true } : {}),
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
        lastUsedAt: r.lastUsedAt,
      }))
      setManagesRoot(!!res.managesRoot)
      setAccessCapability(res.accessCapability ?? 'none')
      settle(mapped)
    } finally {
      if (gen === requestGen.current) setIsLoading(false)
    }
  }, [tenantId, viewKey])

  useEffect(() => { refreshEntries().catch(() => { /* list keeps its last good state */ }) }, [refreshEntries])

  // A write's own refresh reloads the view shown WHEN IT LANDS, never the one its callback was made in: the view key
  // also changes when nothing was switched (the role settling as `my-tenants` answers — 2026-10-22 stagetest trace),
  // and the old view's refresh is a no-op by design, while the new view's load may have started before the write
  // committed — the new key then never appeared until something else refreshed.
  const latestRefresh = useRef(refreshEntries)
  latestRefresh.current = refreshEntries
  const refreshAfterWrite = useCallback(() => latestRefresh.current(), [])

  // Subscribe to keybook realtime events for the active tenant. Any
  // mutation in this tenant — by anyone — triggers a re-fetch so the
  // visible list stays in sync without manual refresh.
  //
  // Skip refreshing while a mutation triggered by *this* tab is
  // already in flight: each write path already awaits its own
  // refreshEntries, so an extra one fired by the same tab's realtime
  // echo just adds latency and (more importantly) can race with
  // dialog-local fetches like ShareDialog's reload(). We only refresh
  // for events triggered by *other* actors.
  const { onKeybookEvent } = useRealtime()
  useEffect(() => {
    if (!tenantId || !userEmail) return
    const callerLower = userEmail.toLowerCase()
    return onKeybookEvent(event => {
      if (event.tenantId !== tenantId) return
      if (event.actorEmail && event.actorEmail === callerLower) return
      refreshEntries().catch(() => {})
    })
  }, [tenantId, userEmail, onKeybookEvent, refreshEntries])

  const createEntry = useCallback(async (
    label: string,
    data: KeybookEntryData,
    options?: {
      fileId?: string
      fileIds?: string[]
      usernameHint?: string
      ownerTeamId?: string
      provider?: string
    },
  ): Promise<string> => {
    if (!tenantId) throw new Error('No tenant')
    // ownerId is server-derived for email-owned entries (the resolved
    // orgEmail from the auth context wins). We only specify it for
    // team-owned entries, where the caller is selecting which of
    // their teams owns the entry.
    const res = await keybookApi.createEntry(tenantId, {
      ownerType: options?.ownerTeamId ? 'team' : 'email',
      ...(options?.ownerTeamId && { ownerId: options.ownerTeamId }),
      sourceLabel: label,
      credentialType: data.credentialType,
      fileId: options?.fileId,
      fileIds: options?.fileIds,
      provider: options?.provider,
      data: { ...data, usernameHint: options?.usernameHint ?? data.usernameHint },
    })
    await refreshAfterWrite()
    return res.entryId
  }, [tenantId, refreshAfterWrite])

  const decryptEntry = useCallback(async (entryId: string): Promise<KeybookEntryData> => {
    const res = await keybookApi.getEntry(entryId)
    // Server-decrypted plaintext is in entry.data.
    return res.entry.data as KeybookEntryData
  }, [])

  /** The value WITH the version it was read at — what an editor saves against. */
  const readEntryForEdit = useCallback(async (entryId: string): Promise<{ data: KeybookEntryData; version: number }> => {
    const res = await keybookApi.getEntry(entryId)
    return { data: res.entry.data as KeybookEntryData, version: res.entry.version }
  }, [])

  const updateEntry = useCallback(async (
    entryId: string,
    data: KeybookEntryData,
    options?: { label?: string; usernameHint?: string; expectedVersion?: number; noteOnly?: boolean },
  ): Promise<void> => {
    try {
      if (options?.noteOnly) {
        await keybookApi.updateEntryNote(entryId, data.note ?? '', options.expectedVersion)
      } else {
        await keybookApi.updateEntryValue(entryId, {
          ...data,
          usernameHint: options?.usernameHint ?? data.usernameHint,
        }, options?.expectedVersion)
      }
      if (options?.label !== undefined) {
        await keybookApi.updateEntryMetadata(entryId, { sourceLabel: options.label })
      }
    } catch (err) {
      // Refresh even when the rename is refused after the value landed (e.g.
      // the viewer was lowered from Manage to Write while the dialog was open)
      // — in the background, so the refusal is reported at once and a refresh
      // failure never replaces it.
      void refreshAfterWrite().catch(() => {})
      throw err
    }
    // A save that landed never reports failure because the list didn't reload
    // (that invites a duplicate retry). Accepted cost: on a failed reload this
    // tab keeps the pre-save list until its next refresh — our own realtime
    // echo is skipped — which the Refresh button or re-entering the deck fixes.
    await refreshAfterWrite().catch(() => {})
  }, [refreshAfterWrite])

  const deleteEntry = useCallback(async (entryId: string): Promise<void> => {
    await keybookApi.deleteEntry(entryId)
    await refreshAfterWrite()
  }, [refreshAfterWrite])

  const bindFile = useCallback(async (entryId: string, fileId: string): Promise<void> => {
    await keybookApi.bindFile(entryId, fileId)
    await refreshAfterWrite()
  }, [refreshAfterWrite])

  const unbindFile = useCallback(async (entryId: string, fileId: string): Promise<void> => {
    await keybookApi.unbindFile(entryId, fileId)
    await refreshAfterWrite()
  }, [refreshAfterWrite])

  return {
    entries,
    isLoading,
    hasLoaded: entriesFor === viewKey,
    loadFailed: failedFor === viewKey,
    managesRoot: entriesFor === viewKey && managesRoot,
    accessCapability: entriesFor === viewKey ? accessCapability : 'none',
    refreshEntries,
    createEntry,
    decryptEntry,
    readEntryForEdit,
    updateEntry,
    deleteEntry,
    bindFile,
    unbindFile,
  }
}
